// ParentPortalInstruments.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

// Best practice: parent does NOT start new forms
const ENABLE_PARENT_START_NEW = false;

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = String(status || 'draft');
  if (s === 'locked') return <Badge tone="purple">locked</Badge>;
  if (s === 'submitted') return <Badge tone="green">submitted</Badge>;
  return <Badge tone="amber">draft</Badge>;
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

function pad2(n) { return String(n).padStart(2, '0'); }

function formatDTShort(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return String(dtStr).replace('T', ' ').slice(0, 16);
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

function calculateAge(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  let months = now.getMonth() - dob.getMonth();

  if (now.getDate() < dob.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }

  if (years <= 0 && months <= 0) return 'Newborn';
  if (years <= 0) return `${months} month${months === 1 ? '' : 's'}`;
  if (years < 3) {
    const totalMonths = years * 12 + months;
    return `${years}y (${totalMonths} mo)`;
  }
  return `${years} years`;
}

export default function ParentPortalInstruments() {
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [childrenError, setChildrenError] = useState('');

  const [studentId, setStudentId] = useState('');

  const [period, setPeriod] = useState('intake'); // intake | exit
  const [statusFilter, setStatusFilter] = useState('draft'); // default
  const [q, setQ] = useState('');

  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  // optional start new (off)
  const [availKind, setAvailKind] = useState('intake');
  const [available, setAvailable] = useState([]);
  const [pickInstrumentId, setPickInstrumentId] = useState('');

  async function loadChildren() {
    setChildrenLoading(true);
    setChildrenError('');
    try {
      // ✅ BE-aligned: you already have GET /api/me/students with parent email fallback join
      const res = await API.get('/me/students');

      // If your backend returns {students:[...]} then handle it; if it returns array, handle that too.
      const arr = Array.isArray(res.data) ? res.data : (res.data?.students || []);
      setChildren(arr);
      if (!studentId && arr.length) setStudentId(String(arr[0].id));

      // If you *do* have a dedicated parent route (/parent/children), you can switch back:
      // const res = await API.get('/parent/children');
      // const arr = Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error('Failed to load parent children:', err);
      setChildrenError(
        'Unable to load child information right now. Please try again later or contact your provider.'
      );
      setChildren([]);
    } finally {
      setChildrenLoading(false);
    }
  }

  async function loadSubmissions(sid = studentId) {
    if (!sid) return;
    setLoading(true);
    try {
      const res = await API.get(`/students/${Number(sid)}/instrument-submissions`, {
        params: { period, status: statusFilter },
      });
      setSubmissions(res.data?.submissions || []);
    } catch (e) {
      console.error('Failed to load submissions:', e);
      const msg = e?.response?.data?.error;
      if (msg) console.warn('Submissions error:', msg);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadAvailable() {
    if (!ENABLE_PARENT_START_NEW) return;
    setLoading(true);
    try {
      const res = await API.get('/instruments/available', {
        params: { active: 1, form_kind: availKind },
      });
      const items = (res.data?.items || []).filter((x) => x.can_use);
      setAvailable(items);
      if (items.length && !items.some((x) => String(x.id) === String(pickInstrumentId))) {
        setPickInstrumentId(String(items[0].id));
      }
      if (!items.length) setPickInstrumentId('');
    } catch (e) {
      console.error(e);
      setAvailable([]);
      setPickInstrumentId('');
    } finally {
      setLoading(false);
    }
  }

  async function startNew() {
    if (!ENABLE_PARENT_START_NEW) return;
    if (!studentId) return alert('Select child first');
    if (!pickInstrumentId) return alert('Select template');
    try {
      const res = await API.post('/instrument-submissions/start', {
        student_id: Number(studentId),
        instrument_id: Number(pickInstrumentId),
        period,
      });
      const sid = res.data?.submission_id;
      if (!sid) return alert('Start failed (no submission_id)');
      window.location.href = `/parent/instrument-submissions/${sid}`;
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to start');
    }
  }

  function openSubmission(id) {
    window.location.href = `/parent/instrument-submissions/${id}`;
  }

  useEffect(() => { loadChildren(); }, []); // eslint-disable-line
  useEffect(() => { if (studentId) loadSubmissions(studentId); }, [studentId, period, statusFilter]); // eslint-disable-line
  useEffect(() => { loadAvailable(); }, [availKind, studentId]); // eslint-disable-line

  const selectedChild = useMemo(
    () => children.find((c) => String(c.id) === String(studentId)) || null,
    [children, studentId]
  );

  const filteredSubs = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return submissions || [];
    return (submissions || []).filter((s) => (
      String(s.instrument_code || '').toLowerCase().includes(t) ||
      String(s.instrument_name || '').toLowerCase().includes(t) ||
      String(s.form_kind || '').toLowerCase().includes(t) ||
      String(s.discipline_code || '').toLowerCase().includes(t) ||
      String(s.status || '').toLowerCase().includes(t)
    ));
  }, [submissions, q]);

  const draftsCount = useMemo(() => {
    if (!Array.isArray(submissions)) return 0;
    return submissions.filter((s) => String(s.status) === 'draft').length;
  }, [submissions]);

  const hasChildren = children && children.length > 0;
  const ageLabel = selectedChild?.dob ? calculateAge(selectedChild.dob) : null;

  const showAll = statusFilter === 'all';
  const toggleShowAll = () => setStatusFilter((p) => (p === 'all' ? 'draft' : 'all'));

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* ✅ Neutral header aligned with InstrumentWorkspace */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">
              Parent Portal — Forms
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {ENABLE_PARENT_START_NEW
                ? 'Select your child, open existing forms, or start a new intake/exit form.'
                : 'Complete assigned drafts here. Your provider starts forms and assigns them to you.'}
            </div>

            {selectedChild ? (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500">
                  Viewing:{' '}
                  <span className="font-semibold text-slate-900">
                    {(selectedChild.last_name || '').trim()}, {(selectedChild.first_name || '').trim()}
                  </span>
                </span>
                {ageLabel ? <Badge tone="slate">{ageLabel}</Badge> : null}
                {selectedChild.uci ? <Badge tone="slate">UCI: {selectedChild.uci}</Badge> : null}
                {selectedChild.telehealth_ok ? <Badge tone="green">Telehealth OK</Badge> : null}
                <Badge tone="amber">Drafts: {draftsCount}</Badge>
              </div>
            ) : null}

            {childrenLoading ? (
              <div className="mt-3 text-sm text-slate-500 animate-pulse">Loading child profiles…</div>
            ) : null}
            {!childrenLoading && childrenError ? (
              <div className="mt-3 px-3 py-2 rounded-xl bg-rose-50 text-rose-800 border border-rose-200 text-sm">
                {childrenError}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={toggleShowAll} disabled={!hasChildren || childrenLoading}>
              {showAll ? 'Show drafts only' : 'Show all statuses'}
            </Button>
            <Button variant="secondary" onClick={() => loadSubmissions(studentId)} disabled={!studentId || loading || childrenLoading}>
              {loading ? 'Loading…' : 'Reload'}
            </Button>
          </div>
        </div>

        {!childrenLoading && !childrenError && hasChildren ? (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
            <div className="lg:col-span-5">
              <label className="text-xs text-slate-600">Child</label>
              <select
                className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              >
                {children.map((c) => {
                  const fullName = `${(c.last_name || '').trim()}, ${(c.first_name || '').trim()}`.replace(/^,\s*/, '');
                  const age = calculateAge(c.dob);
                  return (
                    <option key={c.id} value={c.id}>
                      {fullName || 'Unnamed child'}{age ? ` • ${age}` : ''} • id={c.id}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="text-xs text-slate-600">Period</label>
              <select
                className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <option value="intake">intake</option>
                <option value="exit">exit</option>
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="text-xs text-slate-600">Status</label>
              <select
                className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="draft">draft</option>
                <option value="all">all</option>
                <option value="submitted">submitted</option>
                <option value="locked">locked</option>
              </select>
            </div>

            <div className="lg:col-span-3">
              <label className="text-xs text-slate-600">Search</label>
              <input
                className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="code/name/status…"
              />
            </div>
          </div>
        ) : null}
      </div>

      {!childrenLoading && !childrenError && !hasChildren ? (
        <div className="mt-4 bg-white border rounded-2xl shadow-sm p-4">
          <div className="text-sm text-slate-700 font-semibold">No children linked to this parent account.</div>
          <div className="text-xs text-slate-500 mt-1">
            If you believe this is a mistake, please contact your provider so they can update your records.
          </div>
        </div>
      ) : null}

      {ENABLE_PARENT_START_NEW && hasChildren ? (
        <div className="mt-4 bg-white border rounded-2xl shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <div className="font-semibold text-slate-900">Start new</div>
              <div className="text-xs text-slate-500">
                Uses organization licenses + discipline gating. Only usable templates appear here.
              </div>
            </div>
            <Button onClick={startNew} variant="primary">
              Start {period}
            </Button>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-3">
              <label className="text-xs text-slate-600">Template kind</label>
              <select
                className="w-full px-3 py-2 rounded-xl border bg-white"
                value={availKind}
                onChange={(e) => setAvailKind(e.target.value)}
              >
                <option value="intake">intake</option>
                <option value="instrument">instrument</option>
              </select>
            </div>

            <div className="md:col-span-9">
              <label className="text-xs text-slate-600">Template</label>
              <select
                className="w-full px-3 py-2 rounded-xl border bg-white"
                value={pickInstrumentId}
                onChange={(e) => setPickInstrumentId(e.target.value)}
              >
                {!available.length ? <option value="">No available templates</option> : null}
                {available.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.instrument_code} v{i.version} — {i.name} {i.discipline_code ? `(${i.discipline_code})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : null}

      {hasChildren ? (
        <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-semibold text-slate-900">Assigned submissions</div>
            <Button variant="secondary" onClick={() => loadSubmissions(studentId)} disabled={!studentId || loading}>
              Refresh
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left">
                  <th className="p-3">Template</th>
                  <th className="p-3">Kind</th>
                  <th className="p-3">Period</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Started</th>
                  <th className="p-3">Submitted</th>
                  <th className="p-3 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubs.map((s) => (
                  <tr key={s.id} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      <div className="font-mono text-[12px] text-slate-700">
                        {s.instrument_code} <span className="text-slate-500">v{s.version}</span>
                      </div>
                      <div className="font-medium text-slate-900">{s.instrument_name}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-1">
                        {s.discipline_code ? <Badge>{s.discipline_code}</Badge> : <span className="text-slate-400">—</span>}
                        <Badge tone={s.distribution_mode === 'shell_only' ? 'slate' : 'green'}>
                          {s.distribution_mode}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge tone={s.form_kind === 'intake' ? 'purple' : 'blue'}>{s.form_kind}</Badge>
                    </td>
                    <td className="p-3"><Badge tone="amber">{s.period}</Badge></td>
                    <td className="p-3"><StatusBadge status={s.status} /></td>
                    <td className="p-3">{formatDTShort(s.started_at)}</td>
                    <td className="p-3">{formatDTShort(s.submitted_at)}</td>
                    <td className="p-3 text-right">
                      <Button variant="primary" onClick={() => openSubmission(s.id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}

                {!filteredSubs.length && !loading ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-slate-500">
                      {statusFilter === 'draft'
                        ? 'No draft submissions assigned right now.'
                        : 'No submissions found for this child/filters.'}
                      <div className="text-xs mt-2">
                        If you need a new form, please contact your provider.
                      </div>
                    </td>
                  </tr>
                ) : null}

                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="p-3 text-xs text-slate-500">
            Parents can fill only sections where owner roles include <span className="font-mono">parent</span>.
            Submitted/locked sections are read-only.
          </div>
        </div>
      ) : null}
    </div>
  );
}




